import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data.user);
            setOrganization(response.data.organization);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password, organizationCode = null) => {
        const payload = { email, password };
        if (organizationCode) {
            payload.organization_code = organizationCode;
        }
        const response = await axios.post(`${API}/auth/login`, payload);
        const { token: newToken, user: userData, organization: orgData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        setOrganization(orgData);
        return { user: userData, organization: orgData };
    };

    const register = async (name, email, password, organizationCode) => {
        const response = await axios.post(`${API}/auth/register`, {
            name,
            email,
            password,
            organization_code: organizationCode
        });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        
        // Fetch org info
        const orgResponse = await axios.get(`${API}/organizations/${organizationCode}/public`);
        setOrganization(orgResponse.data);
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setOrganization(null);
    };

    const updatePoints = (newPoints) => {
        if (user) {
            setUser({ ...user, points_balance: newPoints });
        }
    };

    const setAuthData = (newToken, userData, orgData) => {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        setOrganization(orgData);
    };

    const value = {
        user,
        organization,
        token,
        loading,
        login,
        register,
        logout,
        updatePoints,
        setAuthData,
        isAuthenticated: !!user,
        isSuperadmin: user?.role === 'superadmin',
        isOrgAdmin: user?.role === 'org_admin' || user?.role === 'superadmin',
        isSalesRep: user?.role === 'sales_rep',
        isCustomer: user?.role === 'customer'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
